import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Table, TableColumnsType } from 'antd'
import { TableProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePersonsServiceAddGuardianEndpoint,
  usePersonsServiceAddWardEndpoint,
  usePersonsServiceGetGuardianshipListEndpoint,
  usePersonsServiceGetGuardianshipListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  GetPersonByIdnpResponse,
  GetPersonListItemByDataResponse,
  GuardianshipStatus
} from 'src/api/requests'
import { TransferModal } from 'src/features/persons/components/Transfer'
import { useStyles } from 'src/features/persons/styles'
import { GenderShortMap } from 'src/features/persons/utils'
import { useUser } from 'src/hooks'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  isLoading?: boolean
  data?: GetPersonListItemByDataResponse[]
  onCancel?: () => void
} & TableProps

const DEFAULT_USER_LIST_PER_PAGE = 5

export const SearchPersonTable = ({
  data,
  isLoading,
  onCancel,
  ...props
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const { userData } = useUser()

  const [selectedPage, setSelectedPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_USER_LIST_PER_PAGE)

  const [modalState, setModalState] = useState<{
    isOpen: boolean
    data?: GetPersonByIdnpResponse | null
  }>({ data: null, isOpen: false })

  const paginatedSelectedUsers = data?.slice(
    (selectedPage - 1) * pageSize,
    selectedPage * pageSize
  )

  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data: guardianData } = usePersonsServiceGetGuardianshipListEndpoint({
    personId: String(id)
  })

  const { mutate: addGuardian } = usePersonsServiceAddGuardianEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetGuardianshipListEndpointKey, String(id)]
      })
      onCancel && onCancel()
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const { mutate: adWard } = usePersonsServiceAddWardEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetGuardianshipListEndpointKey, String(id)]
      })
      onCancel && onCancel()
    },
    onError: (e: ApiError) => formErrorHandler(e.body.errors)
  })

  const onAddTutor = (personId?: string) => {
    const isGuardian = guardianData?.status === GuardianshipStatus.IS_GUARDIAN

    isGuardian
      ? adWard({
          personId: String(id),
          requestBody: {
            wardId: String(personId)
          }
        })
      : addGuardian({
          personId: String(id),
          requestBody: {
            guardianId: String(personId)
          }
        })
  }

  const columns = useMemo(
    (): TableColumnsType<GetPersonListItemByDataResponse> => [
      {
        key: 'lastName',
        title: t('form.last_name'),
        dataIndex: 'lastName'
      },
      {
        key: 'firstName',
        title: t('form.first_name'),
        dataIndex: 'firstName'
      },
      {
        key: 'idnp',
        title: t('form.idnp'),
        dataIndex: 'idnp'
      },
      {
        key: 'birth_date',
        title: t('form.birth_date'),
        render: (_, { birthDate }) =>
          dayjs(birthDate).format(dayMonthYearFormatWithDot),
        align: 'center'
      },
      {
        key: 'gender',
        title: t('form.gender'),
        render: (_, { gender }) => (gender ? GenderShortMap[gender] : '-'),
        align: 'center',
        width: '84px'
      },
      {
        key: 'institution',
        title: t('common.institution'),
        render: (_, { institution }) => institution?.name ?? '-',
        align: 'center'
      },
      {
        key: 'doctor',
        title: t('common.doctor'),
        render: (_, { doctor }) =>
          doctor ? `${doctor?.firstName} ${doctor?.lastName}` : '-',
        align: 'center'
      },
      {
        key: 'actions',
        title: t('common.action'),
        align: 'center',
        render: (person) => {
          const canTransfer =
            person?.institution?.id !== userData?.currentLegalEntityId

          const onClick = () => {
            if (canTransfer)
              return setModalState({
                isOpen: true,
                data: person
              })

            onAddTutor(person?.id)
          }

          return (
            <Button type="link" onClick={onClick}>
              {t(canTransfer ? 'persons.patient_transfer' : 'common.add')}
            </Button>
          )
        }
      }
    ],
    [t]
  )

  return (
    <Card className={styles.personsCard}>
      <Table
        rowKey="id"
        loading={isLoading}
        dataSource={paginatedSelectedUsers}
        columns={columns}
        pagination={{
          current: selectedPage,
          pageSize,
          total: data?.length,
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setPageSize(size),
          showTotal: (total) => `${t('common.total')} ${total}`,
          onChange: (page) => setSelectedPage(page),
          hideOnSinglePage: true
        }}
        scroll={{ x: 'max-content' }}
        {...props}
      />

      {modalState?.isOpen && modalState?.data && (
        <TransferModal data={modalState.data} onCancel={onCancel} />
      )}
    </Card>
  )
}
